import { useMemo } from 'react';
import { useBatches } from '../hooks/useBatches';
import { BatchList } from '../components/BatchList';

export function Dashboard() {
  const { data: batches, isLoading: batchesLoading } = useBatches();

  const stats = useMemo(() => {
    if (!batches) return null;
    const actionRequiredOrders = batches.reduce((sum, b) => sum + (b.actionRequiredOrders || 0), 0);
    const pendingOrders = batches.reduce((sum, b) => sum + b.pendingOrders, 0);
    return {
      totalOrders: batches.reduce((sum, b) => sum + b.totalOrders, 0),
      resolvedOrders: batches.reduce((sum, b) => sum + b.resolvedOrders, 0),
      actionRequiredOrders,
      paymentPendingOrders: pendingOrders - actionRequiredOrders,
    };
  }, [batches]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Missing Orders Dashboard</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-500">Total Orders</p>
          <p className="mt-1 text-3xl font-semibold text-gray-900">
            {batchesLoading ? (
              <span className="animate-pulse bg-gray-200 h-9 w-20 rounded inline-block" />
            ) : (
              stats?.totalOrders || 0
            )}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-500">Resolved</p>
          <p className="mt-1 text-3xl font-semibold text-green-600">
            {batchesLoading ? (
              <span className="animate-pulse bg-gray-200 h-9 w-20 rounded inline-block" />
            ) : (
              stats?.resolvedOrders || 0
            )}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4">
          <p className="text-sm font-medium text-red-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.345 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            Action Required
          </p>
          <p className="mt-1 text-3xl font-semibold text-red-600">
            {batchesLoading ? (
              <span className="animate-pulse bg-gray-200 h-9 w-20 rounded inline-block" />
            ) : (
              stats?.actionRequiredOrders || 0
            )}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-500">Payment Pending</p>
          <p className="mt-1 text-3xl font-semibold text-yellow-600">
            {batchesLoading ? (
              <span className="animate-pulse bg-gray-200 h-9 w-20 rounded inline-block" />
            ) : (
              stats?.paymentPendingOrders || 0
            )}
          </p>
        </div>
      </div>

      {/* Batch List */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Batches
        </h2>
        <BatchList batches={batches || []} isLoading={batchesLoading} />
      </div>
    </div>
  );
}
